import React, { useState, Suspense, lazy } from 'react';
import { FileDown, Loader2, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import ReportPdfGenerator from '../utils/ReportPdfGenerator';

// Lazy-load the PDF viewer — it pulls in pdfjs-dist (~290 KB gzipped).
// Without this lazy boundary, the whole PDF stack ends up in the eager
// dependency graph of any page that mounts ExportPdfButton (e.g. Dashboard
// and Students, both eager pages in App.jsx). With it, the PDF stack is
// only fetched when the user clicks Export.
const PDFViewerModal = lazy(() => import(/* webpackChunkName: "pdf-viewer-modal" */ './PDFViewerModal'));

/**
 * Enterprise Export PDF Button
 * Matches the app's design system with consistent styling across all pages.
 *
 * @param {string} reportType - 'dashboard' | 'clients' | 'calendar' | 'surveys' | 'teachers' | 'groups' | 'billing'
 * @param {Function} getReportData - Function that returns the data object for the report
 * @param {string} label - Optional custom button label
 * @param {string} variant - 'primary' | 'secondary' | 'outline' | 'icon'
 * @param {object} style - Optional additional styles
 */
export default function ExportPdfButton({ reportType, getReportData, label, variant = 'outline', style = {}, trigger }) {
  const { i18n, t } = useTranslation();
  const { centerType } = useAuthStore();
  const [isGenerating, setIsGenerating] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [pdfViewer, setPdfViewer] = useState({ isOpen: false, blob: null, fileName: '' });

  // Auto-trigger export when trigger prop changes (from Quick Actions)
  React.useEffect(() => {
    if (trigger > 0) handleExport();
  }, [trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    setError(null);

    try {
      const data = await getReportData();
      if (!data) throw new Error('No data available for report');

      const generator = new ReportPdfGenerator();
      const result = await generator.generateReport(reportType, data, i18n.language, centerType);

      if (result.success) {
        setPdfViewer({ isOpen: true, blob: result.blob, fileName: result.filename });
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 2000);
      }
    } catch (err) {
      console.error('PDF export error:', err);
      // The generator's own message is technical English; show the translated one.
      setError(t('student.pdfExportError'));
      setTimeout(() => setError(null), 4000);
    } finally {
      setIsGenerating(false);
    }
  };

  const buttonLabel = label || t('common.exportPdf', 'Export PDF');

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleExport(); }}
        disabled={isGenerating}
        title={buttonLabel}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem',
          cursor: isGenerating ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          transition: 'all 0.2s ease',
          opacity: isGenerating ? 0.7 : 1,
          // Variant-specific styles
          ...(variant === 'primary' ? {
            padding: '0.625rem 1.25rem',
            borderRadius: '0.5rem',
            background: 'var(--color-primary)',
            border: 'none',
            color: 'var(--color-on-primary, #fff)',
            fontSize: '0.875rem',
            fontWeight: '600',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          } : variant === 'secondary' ? {
            padding: '0.625rem 1.25rem',
            borderRadius: '0.5rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            fontWeight: '500',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          } : variant === 'icon' ? {
            padding: '0.5rem',
            borderRadius: '0.5rem',
            background: 'transparent',
            border: '1px solid transparent',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            fontWeight: '500',
          } : {
            // 'outline' — default enterprise style
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            fontWeight: '500',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          }),
          ...style,
        }}
        onMouseEnter={(e) => {
          if (!isGenerating) {
            if (variant === 'primary') {
              e.currentTarget.style.filter = 'brightness(1.1)';
            } else if (variant === 'icon') {
              e.currentTarget.style.background = 'var(--color-surface-alt, #f3f4f6)';
              e.currentTarget.style.borderColor = 'var(--color-border)';
            } else {
              e.currentTarget.style.borderColor = 'var(--color-primary-dark, #0ea5e9)';
              e.currentTarget.style.color = 'var(--color-primary-dark, #0ea5e9)';
            }
          }
        }}
        onMouseLeave={(e) => {
          if (variant === 'primary') {
            e.currentTarget.style.filter = 'none';
          } else if (variant === 'icon') {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'transparent';
          } else {
            e.currentTarget.style.borderColor = 'var(--color-border)';
            e.currentTarget.style.color = 'var(--color-text-secondary)';
          }
        }}
      >
        {isGenerating ? (
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        ) : showSuccess ? (
          <CheckCircle size={16} style={{ color: '#10b981' }} />
        ) : (
          <FileDown size={16} />
        )}
        {variant !== 'icon' && (
          <span>{isGenerating ? t('common.generating', 'Generating...') : buttonLabel}</span>
        )}
      </button>

      {error && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--color-danger-light, #fee2e2)',
          color: 'var(--color-danger-dark, #991b1b)',
          padding: '12px 24px',
          borderRadius: '0.75rem',
          fontSize: '0.875rem',
          fontWeight: '500',
          zIndex: 10000,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxWidth: '90vw',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          animation: 'pdfToastUp 0.3s ease',
        }}>
          {error}
        </div>
      )}

      {pdfViewer.isOpen && (
        <Suspense fallback={null}>
          <PDFViewerModal
            isOpen={pdfViewer.isOpen}
            onClose={() => setPdfViewer({ isOpen: false, blob: null, fileName: '' })}
            pdfBlob={pdfViewer.blob}
            fileName={pdfViewer.fileName}
          />
        </Suspense>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pdfToastUp {
          from { transform: translate(-50%, 20px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
